import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'

import { CALENDAR_READERS } from '../auth/roles.ts'
import { guardRoute } from '../auth/guard.ts'
import {
  MATCH_SCOPES,
  MATCH_VENUES,
  NO_MATCH_FILTERS,
  type MatchFilters,
  type MatchScope,
  type MatchVenue,
} from '../data/match-filters.ts'
import { NAV_PATHS } from '../navigation/paths.ts'
import { parseOneOf, parsePositiveInt } from './params.ts'
import { rootRoute } from './root.tsx'

// The società's whole calendar. `requiredRoles` and not an open guard: the page reads the
// club off `me.appointments`, which a giocatore does not have — the spec's R1.2.

export function parseMatchScope(value: unknown): MatchScope {
  return parseOneOf(MATCH_SCOPES, value) ?? NO_MATCH_FILTERS.scope
}

export function parseMatchVenue(value: unknown): MatchVenue {
  return parseOneOf(MATCH_VENUES, value) ?? NO_MATCH_FILTERS.venue
}

/** An ISO day, `2026-11-01`. Anything else is no bound at all, so a mistyped link shows the
 *  whole calendar instead of an empty one. */
function parseDay(value: unknown): string | null {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  return value
}

export const matchesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_PATHS.matches,
  validateSearch: (search: Record<string, unknown>): MatchFilters => ({
    scope: parseMatchScope(search.scope),
    teamId: parsePositiveInt(search.teamId),
    groupId: parsePositiveInt(search.groupId),
    venue: parseMatchVenue(search.venue),
    from: parseDay(search.from),
    to: parseDay(search.to),
    withPostponed: search.withPostponed === true || search.withPostponed === 'true',
    toPrepare: search.toPrepare === true || search.toPrepare === 'true',
  }),
  search: {
    // Defaults leave the URL: a colleague receives /matches?venue=home, never the whole set.
    middlewares: [stripSearchParams(NO_MATCH_FILTERS)],
  },
  beforeLoad: ({ context, location }) =>
    guardRoute({
      queryClient: context.queryClient,
      requiredRoles: CALENDAR_READERS,
      currentPath: location.href,
    }),
  staticData: { opensOnHero: true },
  component: lazyRouteComponent(() => import('../pages/matches.tsx')),
})

// A literal and not `matchesRoute.to`: a link constant is evaluated when the module loads,
// and `to` is still empty then — `routes/teams.tsx`'s reason.
export const matchListLink = { to: '/matches', search: NO_MATCH_FILTERS } as const

/** The Calendar of one squad, every fixture of it: what «Tutte le partite» of its page opens. */
export function matchListOfTeamLink(teamId: number) {
  return { to: '/matches', search: { ...NO_MATCH_FILTERS, scope: 'all', teamId } } as const
}
